import { ApiError, type ApiClient } from '@ghp/api-client'
import type { LoginRequest, SessionUser, TeamChoice } from '@ghp/domain'

import type { TeamScope } from '../../offline/team-scope'
import { clearSession, loadSession, replaceSessionIfUnchanged, saveSession } from './session-store'

export type AuthApi = ApiClient['auth']
type SessionDatabase = Parameters<typeof loadSession>[0]

/**
 * **Ce que la tablette sait du serveur** quand la session n'est pas confirmée — et rien de plus.
 *
 * Les trois valeurs ne sont pas trois nuances d'un même ennui : elles appellent **trois conduites
 * différentes**, et c'est la seule raison de les distinguer.
 *
 * - `not_attempted` — le serveur n'a **rien eu à dire** : aucun rafraîchissement n'est parti
 *   depuis. C'est l'état des premières centaines de millisecondes de chaque démarrage à froid
 *   (`enterFromLocal`), et celui où l'écran ne doit accuser personne. Rien à faire, la passe de
 *   synchro suivante tranchera ;
 * - `unreachable` — un rafraîchissement est parti et **rien d'exploitable n'est revenu** (requête
 *   en échec, `5xx`, réponse hors contrat). La saisie continue, elle partira au retour du
 *   serveur ;
 * - `rejected` — le serveur a **répondu**, et il refuse le jeton de rafraîchissement : expiré
 *   (72 h, M23) ou révoqué. C'est le seul cas où attendre ne sert à rien — il faut ressaisir des
 *   identifiants.
 *
 * ⚠️ Cette distinction ne change **rien** à ce que D22 interdit : quelle que soit la valeur, la
 * session locale n'est ni effacée ni fermée, et l'agent continue sa tournée.
 */
export type SessionDoubt = 'not_attempted' | 'unreachable' | 'rejected'

/**
 * Les trois états d'une tablette (D22 pt.1) :
 * - `signed_out` — aucune session locale : l'écran de connexion est le seul chemin ;
 * - `degraded` — une session locale existe, que le serveur n'a pas confirmée. L'agent travaille,
 *   la synchro attendra. **`reason` dit ce qu'on en sait** (`SessionDoubt`) : sans lui les trois
 *   producteurs posaient le même objet, un écran ne pouvait que taire la cause ou l'inventer
 *   (M21), et le pied du rail accusait un serveur muet au démarrage à froid (#141) ;
 * - `online` — jeton d'accès frais, tout fonctionne.
 */
export type TerrainSession =
  | { state: 'signed_out' }
  | { state: 'degraded'; reason: SessionDoubt; user: SessionUser; team: TeamChoice | null }
  | { state: 'online'; accessToken: string; user: SessionUser; team: TeamChoice | null }

/**
 * Le **périmètre d'équipe** d'une session : pour quelle équipe la tablette lit et range ses
 * données locales (D22 pt.4).
 *
 * Cette conversion vit ici, à côté du type qu'elle lit, et **pas** dans `offline/` : la couche
 * hors-ligne est la plus basse de l'app, elle ne connaît ni la session ni les écrans.
 *
 * `signed_out` rend `unknown`, ce qui couvre **deux** situations du même coup — la session locale
 * pas encore lue (l'état de départ du store, avant `ready`) et une vraie déconnexion. Dans les
 * deux cas aucune équipe n'est connue, donc rien ne doit être lu ni écrit : servir « tout » le
 * temps de cette fenêtre montrerait la tournée de l'équipe d'hier.
 */
export function scopeOf(session: TerrainSession): TeamScope {
  if (session.state === 'signed_out') return { kind: 'unknown' }

  return session.team ? { kind: 'team', id: session.team.id } : { kind: 'no_team' }
}

/**
 * Le serveur a authentifié l'agent, mais la tablette n'a pas pu écrire la session (quota
 * saturé, navigation privée, éviction d'IndexedDB — D22).
 *
 * Un type à part, parce que la panne n'est pas celle qu'on croit : classée avec les échecs
 * réseau, elle envoie l'agent vérifier sa connexion, et chaque nouvel essai réussit côté
 * serveur pour échouer ici — jusqu'à la limite de tentatives, qui l'installe définitivement
 * sur la fausse piste. La cause d'origine est conservée pour le diagnostic.
 */
export class SessionWriteError extends Error {
  override readonly name = 'SessionWriteError'

  constructor(cause: unknown) {
    super("La tablette n'a pas pu enregistrer la session.", { cause })
  }
}

export type SignInResult =
  | { outcome: 'signed_in'; session: Extract<TerrainSession, { state: 'online' }> }
  | { outcome: 'team_selection_required'; teams: TeamChoice[] }

/**
 * L'entrée dans l'app, **sans serveur** : la seule lecture d'IndexedDB décide. C'est ce qui
 * garantit qu'un rechargement hors réseau ne renvoie jamais à l'écran de connexion (D22
 * pt.1). La session rendue est `degraded` — la montée en ligne est l'affaire de
 * `refreshSession`, en arrière-plan, jamais sur le chemin de l'entrée.
 */
export async function enterFromLocal(db: SessionDatabase): Promise<TerrainSession> {
  const stored = await loadSession(db)
  if (!stored) return { state: 'signed_out' }

  return { state: 'degraded', reason: 'not_attempted', user: stored.user, team: stored.team }
}

/**
 * Ce qu'un rafraîchissement en échec apprend sur le serveur — jamais plus que ce qu'il a dit.
 *
 * Un `401` ou un `403` **est une réponse** : le serveur est joignable et il refuse le jeton de
 * rafraîchissement. Tout le reste — requête en échec, `5xx`, réponse hors contrat — ne dit rien
 * de ce jeton, seulement que la tablette n'a pas obtenu de réponse exploitable ; le classer
 * « refusé » enverrait l'agent ressaisir ses identifiants pour une panne de réseau.
 */
function doubtOf(failure: unknown): SessionDoubt {
  const answered = failure instanceof ApiError && (failure.status === 401 || failure.status === 403)

  return answered ? 'rejected' : 'unreachable'
}

/**
 * La montée en ligne — au démarrage, puis à chaque retour du réseau.
 *
 * ⚠️ Un refresh en échec ne déconnecte jamais et n'efface jamais la session — quel que soit
 * l'échec, réseau ou refus. Une donnée de terrain ne se reconstitue pas, et faire dépendre
 * l'entrée de l'agent d'un serveur qui répond est précisément ce que D22 interdit. On repasse
 * en dégradé et on retente à la reconnexion.
 *
 * ⚠️ **Ce qui change avec `reason`, c'est ce que l'écran peut en dire, pas ce que le code en
 * fait** (#141). Un `401` reste sans conséquence sur la session locale : il ne ferme rien, il
 * n'efface rien, il note seulement que **le serveur a répondu** — donc que la tablette n'est
 * pas hors ligne, et qu'attendre le retour du réseau ne réparera rien. C'est au panneau de
 * connexion d'offrir la ressaisie ; ce n'est pas à cette fonction de la forcer.
 */
export async function refreshSession(db: SessionDatabase, api: AuthApi): Promise<TerrainSession> {
  const stored = await loadSession(db)
  if (!stored) return { state: 'signed_out' }

  try {
    const { accessToken, team } = await api.refresh({
      refreshToken: stored.refreshToken,
      // Le serveur revérifie l'appartenance : le client rappelle un choix, il ne
      // s'accorde pas un droit (M23).
      teamId: stored.team?.id,
    })

    // ⚠️ **L'équipe du serveur l'emporte sur celle qu'on avait rangée** (T1). C'est le bureau
    // qui décide des affectations : tant que la tablette rejouait sa propre copie, changer un
    // agent d'équipe n'avait d'effet qu'après une déconnexion/reconnexion — et rien à l'écran
    // ne disait pourquoi la tournée était celle d'hier. `undefined` = le canal n'a pas
    // d'équipe à donner : on garde alors ce qu'on avait, plutôt que d'effacer sur un silence.
    const current = team === undefined ? stored.team : team

    // Le périmètre local est **la clé de lecture** de toute la base (`offline/team-scope.ts`) :
    // il doit survivre au rechargement, sinon le prochain démarrage repartirait de l'ancienne
    // équipe avant même d'avoir joint le serveur. L'échec d'écriture ne coupe pas la session en
    // vol — celle de cet instant est juste, c'est la suivante qui repartirait de travers.
    //
    // ⚠️ **Seulement si la base porte encore ce refresh** : la réponse a pu mettre longtemps, et
    // l'agent s'est peut-être déconnecté entre-temps, ou un autre connecté. Réécrire alors
    // rouvrirait la session quittée au prochain démarrage, ou effacerait celle de l'entrant.
    if (current?.id !== stored.team?.id) {
      try {
        await replaceSessionIfUnchanged(db, stored.refreshToken, {
          refreshToken: stored.refreshToken,
          user: stored.user,
          team: current,
        })
      } catch {
        // Rien à faire de plus ici : `loadSession` relira l'ancienne équipe au prochain
        // démarrage, et ce rafraîchissement-là la corrigera de nouveau.
      }
    }

    return { state: 'online', accessToken, user: stored.user, team: current }
  } catch (failure) {
    return {
      state: 'degraded',
      reason: doubtOf(failure),
      user: stored.user,
      team: stored.team,
    }
  }
}

/**
 * La connexion, en deux temps quand il le faut (M6) : un `team_selection_required` remonte
 * tel quel à l'écran, qui rappellera `signIn` avec le `teamId` choisi. Rien n'est persisté
 * tant qu'aucun jeton n'est émis.
 */
export async function signIn(
  db: SessionDatabase,
  api: AuthApi,
  credentials: LoginRequest,
): Promise<SignInResult> {
  const response = await api.login(credentials)

  if (response.status === 'team_selection_required') {
    return { outcome: 'team_selection_required', teams: response.teams }
  }

  const { accessToken, refreshToken, user, team } = response

  // Le refresh du terrain arrive dans le corps, précisément pour finir en IndexedDB (D3).
  // Son absence signifierait une réponse de canal back-office : on laisse entrer (M21,
  // avertir plutôt que bloquer) mais la session ne survivra pas à un rechargement.
  if (refreshToken !== undefined) {
    try {
      await saveSession(db, { refreshToken, user, team: team ?? null })
    } catch (failure) {
      throw new SessionWriteError(failure)
    }
  }

  return {
    outcome: 'signed_in',
    session: { state: 'online', accessToken, user, team: team ?? null },
  }
}

/**
 * La déconnexion volontaire — le seul chemin qui efface la session locale. La révocation
 * serveur est un meilleur effort : hors réseau, elle échoue et ce n'est pas bloquant, le
 * refresh expirera de lui-même à 72 h (M23).
 *
 * ⚠️ **Ce geste ne touche QUE la base de session.** Rien n'est effacé dans les bases de tournée —
 * ni demande, ni brouillon, ni photo, ni envoi en file. L'équipe suivante ne les voit pas parce
 * que **sa** base est une autre (D22 pt.4, `offline/team-data.ts`), pas parce qu'elles ont
 * disparu : elles restent au repos sur la tablette. Ne pas lire « purge »
 * ici, et ne pas « compléter » le cloisonnement en purgeant — une saisie non transmise n'existe
 * nulle part ailleurs.
 */
export async function signOut(db: SessionDatabase, api: AuthApi): Promise<TerrainSession> {
  const stored = await loadSession(db)

  if (stored) {
    try {
      await api.logout({ refreshToken: stored.refreshToken })
    } catch {
      // Tablette hors ligne : la déconnexion locale n'attend pas le serveur.
    }
  }

  await clearSession(db)
  return { state: 'signed_out' }
}
